import { Diagram } from '../../diagram';
import { Rect } from '../../primitives/rect';
import { FlowchartModel } from './flow-chart-model';
import { MatrixModel } from './matrix-model';
/**
 * Defines the Flowchart Layout
 */
export class FlowchartLayout {
    constructor() {
        this.rootNodes = [];
        this.vertexMapper = new Map();
        this.edgesMapper = new Map();
        this.loopedgesMapper = new Map();
        this.anchorX = 0;
        this.anchorY = 0;
        this.verticalSpacing = 50;
        this.horizontalSpacing = 50;
        this.horizontalAlignment = 'Center';
        this.verticalAlignment = 'Top';
        this.margin = { top: 50, right: 50, bottom: 50, left: 50 };
        this.orientation = 'TopToBottom';
        this.yesBranchDirection = 'SameAsFlow';
        this.noBranchDirection = 'RightInFlow';
        this.yesBranchValues = ['Yes', 'True'];
        this.noBranchValues = ['No', 'False'];
        this.diagram = new Diagram();
    }
    /**
     * To update the layout of the diagram.
     * @private
     * @param {NodeModel[]} nodes - provide the node value.
     * @param {Diagram} diagram - provide the diagram value.
     * @returns { void }
     */
    updateLayout(nodes, diagram) {
        this.diagram = diagram;
        this.yesBranchDirection = this.diagram.layout.flowchartLayoutSettings.yesBranchDirection;
        this.noBranchDirection = this.diagram.layout.flowchartLayoutSettings.noBranchDirection;
        this.yesBranchValues = this.diagram.layout.flowchartLayoutSettings.yesBranchValues || ['Yes', 'True'];
        this.noBranchValues = this.diagram.layout.flowchartLayoutSettings.noBranchValues || ['No', 'False'];
        this.orientation = this.diagram.layout.orientation === 'TopToBottom' || this.diagram.layout.orientation === 'BottomToTop' ? 'TopToBottom' : 'LeftToRight';
        this.horizontalAlignment = this.diagram.layout.horizontalAlignment;
        this.verticalAlignment = this.diagram.layout.verticalAlignment;
        this.verticalSpacing = this.diagram.layout.verticalSpacing;
        this.horizontalSpacing = this.diagram.layout.horizontalSpacing;
        this.margin = this.diagram.layout.margin;
        const firstLevelNodes = [];
        const unseenVertices = [];
        this.rootNodes = [];
        this.vertexMapper.clear();
        this.edgesMapper.clear();
        this.loopedgesMapper.clear();
        for (const item of nodes) {
            if (!item.excludeFromLayout) {
                const vertex = this.createVertex(item);
                this.vertexMapper.set(vertex.id, vertex);
                unseenVertices.push(vertex);
                if (!vertex.inEdges || vertex.inEdges.length === 0) {
                    firstLevelNodes.push(vertex);
                    this.rootNodes.push(item);
                }
            }
        }
        let previousModel = null;
        for (const firstLevelNode of firstLevelNodes) {
            const vertexSet = [];
            this.getTreeVertices(firstLevelNode, vertexSet, unseenVertices);
            const layoutModel = new FlowchartModel(this, firstLevelNode, vertexSet);
            layoutModel.layeringStage();
            const matrixModel = new MatrixModel(layoutModel);
            matrixModel.siblingModel = previousModel;
            matrixModel.arrangeElements();
            previousModel = matrixModel;
        }
        const vertices = Array.from(this.vertexMapper.values());
        const modelBounds = this.getModelBounds(vertices);
        this.updateAnchor(modelBounds);
        const isHorizontal = this.orientation === 'LeftToRight';
        const inverseSpacing = !isHorizontal ? this.verticalSpacing : this.horizontalSpacing;
        const nodeWithMultiEdges = [];
        for (const vertex of vertices) {
            if (vertex) {
                const node = vertex.item;
                if (node) {
                    node.offsetX = vertex.geometry.x + (vertex.geometry.width / 2) + this.anchorX;
                    node.offsetY = vertex.geometry.y + (vertex.geometry.height / 2) + this.anchorY;
                    if ((vertex.inEdges && vertex.inEdges.length > 0) || (vertex.outEdges && vertex.outEdges.length > 0)) {
                        nodeWithMultiEdges.push(node);
                    }
                }
                diagram.dataBind();
            }
        }
        const transModelBounds = new Rect(modelBounds.x + this.anchorX, modelBounds.y
            + this.anchorY, modelBounds.width, modelBounds.height);
        this.nodeWithMultiEdges = nodeWithMultiEdges;
        this.inverseSpacing = inverseSpacing;
        this.transModelBounds = transModelBounds;
        this.diagram.layout.flowChartData = this;
    }
    /**
     * To re-rout the flowchart connectors.
     * @private
     * @param {FlowchartLayout} layoutData - provide the layoutData value.
     * @param {Diagram} diagram - provide the diagram value.
     * @returns { void }
     */
    reRouteFlowChartConnectors(layoutData, diagram) {
        this.diagram = diagram;
        const nodeWithMultiEdges = layoutData.nodeWithMultiEdges;
        const inverseSpacing = layoutData.inverseSpacing;
        this.orientation = layoutData.orientation;
        const isVertical = this.orientation === 'TopToBottom';
        const transModelBounds = layoutData.transModelBounds;
        this.vertexMapper = layoutData.vertexMapper;
        this.loopedgesMapper = layoutData.loopedgesMapper;
        this.edgesMapper = layoutData.edgesMapper;
        this.anchorX = layoutData.anchorX;
        this.anchorY = layoutData.anchorY;
        const modifiedConnectors = [];
        for (const node of nodeWithMultiEdges) {
            if (node.outEdges && node.outEdges.length > 0) {
                for (const edge of node.outEdges) {
                    const internalConnector = this.diagram.nameTable[`${edge}`];
                    if (this.loopedgesMapper.has(internalConnector) && this.loopedgesMapper.get(internalConnector)) {
                        if (modifiedConnectors.indexOf(internalConnector) === -1) {
                            this.updateLoopConnector(internalConnector);
                            modifiedConnectors.push(internalConnector);
                        }
                    }
                    else {
                        let updatedPts = [];
                        if (node.outEdges.length > 1) {
                            const segmentSize = inverseSpacing / 2.0;
                            let intermediatePoint = null;
                            if (this.edgesMapper.has(internalConnector)) {
                                const edgePt = this.edgesMapper.get(internalConnector)[0];
                                if (edgePt) {
                                    intermediatePoint = { x: edgePt.x + this.anchorX, y: edgePt.y + this.anchorY };
                                }
                            }
                            internalConnector.segments = [];
                            internalConnector.intermediatePoints = [];
                            let pts = [internalConnector.sourcePoint, internalConnector.targetPoint];
                            if (isVertical) {
                                updatedPts = this.updateVerticalConnectorSegments(internalConnector, pts);
                                const sourceNode = this.diagram.nameTable[internalConnector.sourceID];
                                const decisionNode = this.vertexMapper.get(sourceNode.id).isDecisionNode;
                                if (!decisionNode && updatedPts.length <= 2) {
                                    pts = this.updateConnectorPoints(updatedPts, segmentSize, intermediatePoint, transModelBounds);
                                }
                                else {
                                    pts = updatedPts;
                                }
                            }
                            else {
                                updatedPts = this.updateHorizontalSegments(internalConnector, pts);
                                pts = updatedPts;
                            }
                            if (pts.length > 2) {
                                this.updatePoints(pts, internalConnector);
                            }
                            modifiedConnectors.push(internalConnector);
                        }
                        else if (internalConnector.intermediatePoints.length === 4) {
                            // Determine updated points based on orientation (vertical or horizontal)
                            const start = internalConnector.intermediatePoints[0];
                            const end = internalConnector.intermediatePoints[3];
                            const offsetPoint = isVertical
                                ? { x: start.x, y: end.y - 20 }
                                : { x: end.x - 20, y: start.y };
                            const updatedPts = [start, offsetPoint, end];
                            // Update connector points and add to modified list
                            this.updatePoints(updatedPts, internalConnector);
                            modifiedConnectors.push(internalConnector);
                        }
                    }
                }
            }
            if (node.inEdges && node.inEdges.length > 1) {
                for (const edge of node.inEdges) {
                    const internalConnector = this.diagram.nameTable[`${edge}`];
                    if (modifiedConnectors.indexOf(internalConnector) === -1) {
                        internalConnector.segments[0].points = [];
                        if (this.loopedgesMapper.has(internalConnector) && this.loopedgesMapper.get(internalConnector)) {
                            this.updateLoopConnector(internalConnector);
                            modifiedConnectors.push(internalConnector);
                        }
                        else {
                            if (node.inEdges.length > 1) {
                                const segmentSize = inverseSpacing / 2.0;
                                let intermediatePoint = null;
                                if (this.edgesMapper.has(internalConnector) && modifiedConnectors.indexOf(internalConnector) === -1) {
                                    const edgePt = this.edgesMapper.get(internalConnector)[0];
                                    if (edgePt) {
                                        intermediatePoint = { x: edgePt.x + this.anchorX, y: edgePt.y + this.anchorY };
                                    }
                                }
                                internalConnector.segments = [];
                                let pts = [internalConnector.targetPoint, internalConnector.sourcePoint];
                                let updatedPts = [];
                                if (isVertical) {
                                    updatedPts = this.updateVerticalConnectorSegments(internalConnector, pts);
                                }
                                else {
                                    updatedPts = this.updateHorizontalSegments(internalConnector, pts);
                                }
                                pts = this.updateConnectorPoints(updatedPts, segmentSize, intermediatePoint, transModelBounds);
                                pts.reverse();
                                if (pts.length > 2) {
                                    this.updatePoints(pts, internalConnector);
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    updateAnchor(bounds) {
        const viewPort = {
            width: this.diagram.scrollSettings.viewPortWidth,
            height: this.diagram.scrollSettings.viewPortHeight
        };
        if (this.orientation === 'TopToBottom') {
            this.anchorX = viewPort.width / 2 - bounds.width / 2 - bounds.x;
            this.anchorY = this.margin.top;
        }
        else {
            this.anchorX = this.margin.left;
            this.anchorY = viewPort.height / 2 - bounds.height / 2 - bounds.y;
        }
        if (this.rootNodes.length === 1) {
            const fixedNode = this.rootNodes[0]; // Assuming rootNodes is defined elsewhere
            const fixedNodeGeometry = this.vertexMapper.get(fixedNode.id).geometry; // Assuming vertexMapper and its usage are defined elsewhere
            const offsetX = fixedNodeGeometry.x + fixedNodeGeometry.width / 2;
            const offsetY = fixedNodeGeometry.y + fixedNodeGeometry.height / 2;
            const dx = offsetX - (bounds.x + bounds.width / 2);
            const dy = offsetY - (bounds.y + bounds.height / 2);
            if (this.orientation === 'TopToBottom') {
                this.anchorX -= dx;
            }
            else {
                this.anchorY -= dy;
            }
        }
    }
    updateConnectorPoints(connectorPoints, startSegmentSize, intermediatePoint, layoutBounds) {
        const isHorizontal = this.orientation === 'LeftToRight';
        const pts = [...connectorPoints];
        // Helper function to find angle between two points
        function findAngle(point1, point2) {
            return Math.atan2(point2.y - point1.y, point2.x - point1.x) * (180 / Math.PI);
        }
        // Function to find the distance (length) between two points
        function findLength(point1, point2) {
            const dx = point2.x - point1.x;
            const dy = point2.y - point1.y;
            return Math.sqrt(dx * dx + dy * dy);
        }
        // Helper function to transform a point
        function transform(point, length, angle) {
            const rad = angle * (Math.PI / 180);
            const newX = point.x + length * Math.cos(rad);
            const newY = point.y + length * Math.sin(rad);
            return { x: newX, y: newY };
        }
        if (pts.length > 2) {
            const newPt = transform(pts[0], startSegmentSize, findAngle(pts[0], pts[1]));
            const nextPt = transform(newPt, findLength(newPt, pts[1]), findAngle(newPt, pts[2]));
            pts.splice(1, 0, nextPt);
            pts.splice(1, 0, newPt);
            pts.splice(3, 2);
            if (intermediatePoint != null) {
                const index = 2;
                const ptsCount = pts.length;
                const newPt1 = transform(pts[ptsCount - 1], startSegmentSize, findAngle(pts[ptsCount - 1], pts[ptsCount - 2]));
                pts.splice(ptsCount - 1, 0, newPt1);
                while (index < pts.length - 2) {
                    pts.splice(index, 1);
                }
                const edgePt = intermediatePoint;
                this.inflate(layoutBounds, layoutBounds.width, layoutBounds.height);
                if (isHorizontal) {
                    const line1 = [{ x: layoutBounds.left, y: edgePt.y }, { x: layoutBounds.right, y: edgePt.y }];
                    const line2 = [{ x: pts[1].x, y: layoutBounds.top }, { x: pts[1].x, y: layoutBounds.bottom }];
                    const line3 = [{ x: newPt1.x, y: layoutBounds.top }, { x: newPt1.x, y: layoutBounds.bottom }];
                    let intercepts1 = [];
                    let intercepts2 = [];
                    // Dummy function calls, replace with actual implementation or mock
                    intercepts1 = this.diagram.commandHandler.intersect(line1, line2, false);
                    intercepts2 = this.diagram.commandHandler.intersect(line1, line3, false);
                    if (intercepts2.length) {
                        pts.splice(2, 0, intercepts2[0]);
                    }
                    if (intercepts1.length) {
                        pts.splice(2, 0, intercepts1[0]);
                    }
                }
                else {
                    const line1 = [{ x: edgePt.x, y: layoutBounds.top }, { x: edgePt.x, y: layoutBounds.bottom }];
                    const line2 = [{ x: layoutBounds.left, y: pts[1].y }, { x: layoutBounds.right, y: pts[1].y }];
                    const line3 = [{ x: layoutBounds.left, y: newPt1.y }, { x: layoutBounds.right, y: newPt1.y }];
                    let intercepts1 = [];
                    let intercepts2 = [];
                    // Dummy function calls, replace with actual implementation or mock
                    intercepts1 = this.diagram.commandHandler.intersect(line1, line2, false);
                    intercepts2 = this.diagram.commandHandler.intersect(line1, line3, false);
                    if (intercepts2.length) {
                        pts.splice(2, 0, intercepts2[0]);
                    }
                    if (intercepts1.length) {
                        pts.splice(2, 0, intercepts1[0]);
                    }
                }
            }
        }
        else if (pts.length === 2 && intermediatePoint != null) {
            const startPt = pts[0];
            const endPt = pts[1];
            const lineAngle = findAngle(pts[0], pts[1]);
            const newPt1 = transform(startPt, startSegmentSize, lineAngle);
            const newPt2 = transform(endPt, startSegmentSize, (lineAngle + 180) % 360);
            pts.splice(1, 0, newPt2);
            if (isHorizontal) {
                const nextPt1 = { x: newPt1.x, y: intermediatePoint.y };
                const nextPt2 = { x: newPt2.x, y: intermediatePoint.y };
                pts.splice(1, 0, nextPt2);
                pts.splice(1, 0, nextPt1);
            }
            else {
                const nextPt1 = { x: intermediatePoint.x, y: newPt1.y };
                const nextPt2 = { x: intermediatePoint.x, y: newPt2.y };
                pts.splice(1, 0, nextPt2);
                pts.splice(1, 0, nextPt1);
            }
            pts.splice(1, 0, newPt1);
        }
        return pts;
    }
    inflate(rect, width, height) {
        rect.x -= width;
        rect.y -= height;
        rect.width += 2 * width;
        rect.height += 2 * height;
    }
    static isBranchConnector(internalConnector, branchValues) {
        if (internalConnector.annotations.length > 0 && internalConnector.annotations[0].content) {
            const text = internalConnector.annotations[0].content;
            return branchValues.some((branchText) => text.localeCompare(branchText, undefined, { sensitivity: 'accent' }) === 0);
        }
        return false;
    }
    isYesBranchConnector(internalConnector) {
        return FlowchartLayout.isBranchConnector(internalConnector, this.yesBranchValues);
    }
    isNoBranchConnector(internalConnector) {
        return FlowchartLayout.isBranchConnector(internalConnector, this.noBranchValues);
    }
    updateHorizontalSegments(internalConnector, pts) {
        let updatedPts = [];
        const sourcenode = this.diagram.nameTable[internalConnector.sourceID];
        const targetnode = this.diagram.nameTable[internalConnector.targetID];
        const decisionNode = this.vertexMapper.get(sourcenode.id).isDecisionNode;
        const hSpacing = this.horizontalSpacing / 2;
        const vSpacing = this.verticalSpacing / 2;
        if (decisionNode) {
            const isYesBranch = this.isYesBranchConnector(internalConnector);
            const isNoBranch = this.isNoBranchConnector(internalConnector);
            if ((!targetnode.wrapper.bounds.containsPoint({ x: targetnode.offsetX, y: sourcenode.offsetY })) &&
                !((sourcenode.offsetY !== targetnode.offsetY) &&
                    ((isYesBranch && this.yesBranchDirection === 'SameAsFlow') ||
                        (isNoBranch && this.noBranchDirection === 'SameAsFlow' && this.yesBranchDirection !== 'SameAsFlow')))) {
                if (sourcenode.wrapper.bounds.bottom < targetnode.wrapper.bounds.center.y) {
                    const spoint1 = sourcenode.wrapper.bounds.bottom;
                    const spoint2 = sourcenode.offsetX;
                    const tpoint1 = targetnode.wrapper.bounds.left;
                    const tpoint2 = targetnode.offsetY;
                    updatedPts.push({ x: spoint2, y: spoint1 });
                    updatedPts.push({ x: spoint2, y: tpoint2 });
                    updatedPts.push({ x: tpoint1, y: tpoint2 });
                }
                else if (sourcenode.wrapper.bounds.top > targetnode.wrapper.bounds.center.y) {
                    const spoint1 = sourcenode.wrapper.bounds.top;
                    const spoint2 = sourcenode.offsetX;
                    const tpoint1 = targetnode.wrapper.bounds.left;
                    const tpoint2 = targetnode.offsetY;
                    updatedPts.push({ x: spoint2, y: spoint1 });
                    updatedPts.push({ x: spoint2, y: tpoint2 });
                    updatedPts.push({ x: tpoint1, y: tpoint2 });
                }
                else if ((isYesBranch && this.yesBranchDirection === 'RightInFlow') ||
                    (isNoBranch && ((this.yesBranchDirection === 'SameAsFlow' &&
                        (this.noBranchDirection === 'RightInFlow' || this.noBranchDirection === 'SameAsFlow')) ||
                        (this.yesBranchDirection === 'LeftInFlow' &&
                            (this.noBranchDirection === 'LeftInFlow' || this.noBranchDirection === 'RightInFlow'))))) {
                    const spoint1 = sourcenode.offsetX;
                    const spoint2 = sourcenode.wrapper.bounds.bottom;
                    const tpoint1 = targetnode.wrapper.bounds.left;
                    const tpoint2 = targetnode.wrapper.bounds.center.y;
                    updatedPts.push({ x: spoint1, y: spoint2 });
                    updatedPts.push({ x: spoint1, y: spoint2 + vSpacing });
                    updatedPts.push({ x: tpoint1 - hSpacing, y: spoint2 + vSpacing });
                    updatedPts.push({ x: tpoint1 - hSpacing, y: tpoint2 });
                    updatedPts.push({ x: tpoint1, y: tpoint2 });
                }
            }
            else if ((isYesBranch && this.yesBranchDirection === 'LeftInFlow') ||
                (isNoBranch && ((this.yesBranchDirection === 'SameAsFlow' &&
                    (this.noBranchDirection === 'SameAsFlow' || this.noBranchDirection === 'LeftInFlow')) ||
                    (this.yesBranchDirection === 'RightInFlow' &&
                        (this.noBranchDirection === 'LeftInFlow' || this.noBranchDirection === 'RightInFlow'))))) {
                const spoint1 = sourcenode.offsetX;
                const spoint2 = sourcenode.wrapper.bounds.top;
                const tpoint1 = targetnode.wrapper.bounds.left;
                const tpoint2 = targetnode.wrapper.bounds.center.y;
                updatedPts.push({ x: spoint1, y: spoint2 });
                updatedPts.push({ x: spoint1, y: spoint2 - vSpacing });
                updatedPts.push({ x: tpoint1 - hSpacing, y: spoint2 - vSpacing });
                updatedPts.push({ x: tpoint1 - hSpacing, y: tpoint2 });
                updatedPts.push({ x: tpoint1, y: tpoint2 });
            }
            else if ((sourcenode.offsetY !== targetnode.offsetY) &&
                ((isYesBranch && this.yesBranchDirection === 'SameAsFlow') ||
                    (isNoBranch && this.noBranchDirection === 'SameAsFlow' &&
                        this.yesBranchDirection !== 'SameAsFlow'))) {
                const spoint1 = sourcenode.wrapper.bounds.right;
                const spoint2 = sourcenode.offsetY;
                const tpoint1 = targetnode.wrapper.bounds.left;
                const tpoint2 = targetnode.wrapper.bounds.center.y;
                updatedPts.push({ x: spoint1, y: spoint2 });
                updatedPts.push({ x: tpoint1 - hSpacing, y: spoint2 });
                updatedPts.push({ x: tpoint1 - hSpacing, y: tpoint2 });
                updatedPts.push({ x: tpoint1, y: tpoint2 });
            }
        }
        else {
            updatedPts = pts;
        }
        return updatedPts;
    }
    updateVerticalConnectorSegments(internalConnector, pts) {
        let updatedPts = [];
        const sourcenode = this.diagram.nameTable[internalConnector.sourceID];
        const targetnode = this.diagram.nameTable[internalConnector.targetID];
        const decisionNode = this.vertexMapper.get(sourcenode.id).isDecisionNode;
        const hSpacing = this.horizontalSpacing / 2;
        const vSpacing = this.verticalSpacing / 2;
        if (decisionNode) {
            const isYesBranch = this.isYesBranchConnector(internalConnector);
            const isNoBranch = this.isNoBranchConnector(internalConnector);
            if ((sourcenode.wrapper.bounds.right < targetnode.wrapper.bounds.center.x) &&
                ((isYesBranch && this.yesBranchDirection === 'RightInFlow') ||
                    (isNoBranch && ((this.yesBranchDirection === 'SameAsFlow' &&
                        (this.noBranchDirection === 'RightInFlow' || this.noBranchDirection === 'SameAsFlow')) ||
                        (this.yesBranchDirection === 'LeftInFlow' &&
                            (this.noBranchDirection === 'LeftInFlow' || this.noBranchDirection === 'RightInFlow')))))) {
                const spoint1 = sourcenode.wrapper.bounds.right;
                const spoint2 = sourcenode.offsetY;
                const tpoint1 = targetnode.wrapper.bounds.top;
                const tpoint2 = targetnode.offsetY;
                updatedPts.push({ x: spoint1, y: spoint2 });
                let overlappingNodes = this.diagram.nodes.filter((e) => e.wrapper.bounds.containsPoint({ x: targetnode.offsetX, y: sourcenode.offsetY }));
                overlappingNodes = overlappingNodes.sort((a, b) => b.wrapper.bounds.left - a.wrapper.bounds.left);
                if (overlappingNodes.length === 0) {
                    updatedPts.push({ x: targetnode.offsetX, y: spoint2 });
                }
                else {
                    const bounds = overlappingNodes[0].wrapper.bounds;
                    updatedPts.push({ x: bounds.left - hSpacing, y: overlappingNodes[0].offsetY });
                    updatedPts.push({ x: bounds.left - hSpacing, y: bounds.bottom + vSpacing });
                }
                updatedPts.push({ x: tpoint1, y: tpoint2 });
            }
            else if ((sourcenode.wrapper.bounds.left > targetnode.wrapper.bounds.center.x) &&
                ((isYesBranch && this.yesBranchDirection === 'LeftInFlow') ||
                    (isNoBranch && ((this.yesBranchDirection === 'SameAsFlow' &&
                        (this.noBranchDirection === 'SameAsFlow' || this.noBranchDirection === 'LeftInFlow')) ||
                        (this.yesBranchDirection === 'RightInFlow' &&
                            (this.noBranchDirection === 'LeftInFlow' || this.noBranchDirection === 'RightInFlow')))))) {
                const spoint1 = sourcenode.wrapper.bounds.left;
                const spoint2 = sourcenode.offsetY;
                const tpoint1 = targetnode.wrapper.bounds.top;
                const tpoint2 = targetnode.offsetY;
                updatedPts.push({ x: spoint1, y: spoint2 });
                const middleRect = Rect.toBounds([updatedPts[0], { x: targetnode.offsetX, y: spoint2 }]);
                let overlappingNodes = this.diagram.nodes.filter((e) => e.wrapper.bounds.intersects(middleRect) &&
                    e.id !== sourcenode.id &&
                    e.id !== targetnode.id);
                overlappingNodes = overlappingNodes.sort((a, b) => b.wrapper.bounds.right - a.wrapper.bounds.right);
                if (overlappingNodes.length === 0) {
                    updatedPts.push({ x: targetnode.offsetX, y: spoint2 });
                }
                else {
                    const bounds = overlappingNodes[0].wrapper.bounds;
                    updatedPts.push({ x: bounds.right + hSpacing, y: overlappingNodes[0].offsetY });
                    updatedPts.push({ x: bounds.right + hSpacing, y: bounds.bottom + vSpacing });
                }
                updatedPts.push({ x: tpoint1, y: tpoint2 });
            }
            else if ((isYesBranch && this.yesBranchDirection === 'RightInFlow') ||
                (isNoBranch && ((this.yesBranchDirection === 'SameAsFlow' &&
                    (this.noBranchDirection === 'RightInFlow' || this.noBranchDirection === 'SameAsFlow')) ||
                    (this.yesBranchDirection === 'LeftInFlow' &&
                        (this.noBranchDirection === 'LeftInFlow' || this.noBranchDirection === 'RightInFlow'))))) {
                const spoint1 = sourcenode.wrapper.bounds.right;
                const spoint2 = sourcenode.offsetY;
                const tpoint1 = targetnode.wrapper.bounds.topCenter.x;
                const tpoint2 = targetnode.wrapper.bounds.topCenter.y;
                updatedPts.push({ x: spoint1, y: spoint2 });
                updatedPts.push({ x: spoint1 + hSpacing, y: spoint2 });
                updatedPts.push({ x: spoint1 + hSpacing, y: tpoint2 - vSpacing });
                updatedPts.push({ x: tpoint1, y: tpoint2 - vSpacing });
                updatedPts.push({ x: tpoint1, y: tpoint2 });
                const middleRect = Rect.toBounds([updatedPts[1], updatedPts[2]]);
                let overlappingNodes = this.diagram.nodes.filter((e) => e.wrapper.bounds.intersects(middleRect));
                overlappingNodes = overlappingNodes.sort((a, b) => b.wrapper.bounds.right - a.wrapper.bounds.right);
                if (overlappingNodes.length > 0 && overlappingNodes[0].wrapper.bounds.intersects(middleRect)) {
                    const bounds = overlappingNodes[0].wrapper.bounds;
                    updatedPts[1].x = bounds.right + hSpacing;
                    updatedPts[2].x = bounds.right + hSpacing;
                }
            }
            else if ((isYesBranch && this.yesBranchDirection === 'LeftInFlow') ||
                (isNoBranch && ((this.yesBranchDirection === 'SameAsFlow' &&
                    (this.noBranchDirection === 'SameAsFlow' || this.noBranchDirection === 'LeftInFlow')) ||
                    (this.yesBranchDirection === 'RightInFlow' &&
                        (this.noBranchDirection === 'LeftInFlow' || this.noBranchDirection === 'RightInFlow'))))) {
                const spoint1 = sourcenode.wrapper.bounds.left;
                const spoint2 = sourcenode.offsetY;
                const tpoint1 = targetnode.wrapper.bounds.topCenter.x;
                const tpoint2 = targetnode.wrapper.bounds.topCenter.y;
                updatedPts.push({ x: spoint1, y: spoint2 });
                updatedPts.push({ x: spoint1 - hSpacing, y: spoint2 });
                updatedPts.push({ x: spoint1 - hSpacing, y: tpoint2 - vSpacing });
                updatedPts.push({ x: tpoint1, y: tpoint2 - vSpacing });
                updatedPts.push({ x: tpoint1, y: tpoint2 });
                const middleRect = Rect.toBounds([updatedPts[1], updatedPts[2]]);
                let overlappingNodes = this.diagram.nodes.filter((e) => e.wrapper.bounds.intersects(middleRect));
                overlappingNodes = overlappingNodes.sort((a, b) => b.wrapper.bounds.left - a.wrapper.bounds.left);
                if (overlappingNodes.length > 0 && overlappingNodes[0].wrapper.bounds.intersects(middleRect)) {
                    const bounds = overlappingNodes[0].wrapper.bounds;
                    updatedPts[1].x = bounds.left - hSpacing;
                    updatedPts[2].x = bounds.left - hSpacing;
                }
            }
            else if ((sourcenode.offsetX !== targetnode.offsetX) &&
                ((isYesBranch && this.yesBranchDirection === 'SameAsFlow') ||
                    (isNoBranch &&
                        this.noBranchDirection === 'SameAsFlow' &&
                        this.yesBranchDirection !== 'SameAsFlow'))) {
                const spoint1 = sourcenode.offsetX;
                const spoint2 = sourcenode.wrapper.bounds.bottom;
                const tpoint1 = targetnode.wrapper.bounds.topCenter.x;
                const tpoint2 = targetnode.wrapper.bounds.topCenter.y;
                updatedPts.push({ x: spoint1, y: spoint2 });
                updatedPts.push({ x: spoint1, y: tpoint2 - vSpacing });
                updatedPts.push({ x: tpoint1, y: tpoint2 - vSpacing });
                updatedPts.push({ x: tpoint1, y: tpoint2 });
            }
            else {
                updatedPts = pts;
            }
        }
        else {
            updatedPts = pts;
        }
        return updatedPts;
    }
    getModelBounds(nodes) {
        const rect = new Rect(0, 0, 0, 0);
        nodes = Array.from(nodes);
        nodes.forEach((vertex) => {
            const geo = vertex.geometry;
            rect.uniteRect(geo);
        });
        return rect;
    }
    createVertex(node) {
        const nodeWidth = isNaN(node.width) ? node.wrapper.bounds.width : node.width;
        const nodeHeight = isNaN(node.height) ? node.wrapper.bounds.height : node.height;
        const geometry = new Rect(0, 0, nodeWidth, nodeHeight);
        const inEdges = [];
        const outEdges = [];
        let branches = { isYesBranch: false, isNoBranch: false };
        if (node.inEdges != null) {
            for (const edge of node.inEdges) {
                const con = this.diagram.nameTable[`${edge}`];
                if (con) {
                    inEdges.push(con);
                }
            }
        }
        if (node.outEdges != null) {
            for (const edge of node.outEdges) {
                const con = this.diagram.nameTable[`${edge}`];
                if (con) {
                    outEdges.push(con);
                }
            }
        }
        const isYesBranch = branches.isYesBranch;
        const isNoBranch = branches.isNoBranch;
        if (inEdges != null) {
            inEdges.forEach((inEdge) => {
                branches = this.checkForYesOrNoBranch(inEdge, isYesBranch, isNoBranch);
            });
        }
        if (outEdges != null) {
            outEdges.forEach((outEdge) => {
                this.edgesMapper.set(outEdge, []);
                this.loopedgesMapper.set(outEdge, false);
            });
        }
        const vert = {
            id: node.id,
            geometry: geometry,
            inEdges: inEdges,
            layoutObjectId: {},
            outEdges: outEdges,
            item: node,
            isDecisionNode: false,
            isYesChild: branches.isYesBranch,
            isNoChild: branches.isNoBranch
        };
        return vert;
    }
    updatePoints(pts, internalConnector) {
        let pointSets = [];
        const segCollection = [];
        for (let i = 0; i < pts.length; i++) {
            if (pts[i + 2]) {
                pointSets.push(pts[parseInt(i.toString(), 10)]);
                pointSets.push(pts[i + 1]);
                const seg = {
                    type: 'Orthogonal',
                    points: pointSets,
                    length: pointSets[0].x === pointSets[1].x ? Math.abs(pointSets[0].y - pointSets[1].y)
                        : Math.abs(pointSets[0].x - pointSets[1].x),
                    direction: pointSets[0].x === pointSets[1].x ? pointSets[0].y > pointSets[1].y ? 'Top' : 'Bottom'
                        : pointSets[0].x > pointSets[1].x ? 'Left' : 'Right'
                };
                pointSets = [];
                segCollection.push(seg);
            }
        }
        internalConnector.segments = segCollection;
    }
    contains(point, bounds) {
        return (point.x >= bounds.left && point.x <= bounds.right && point.y >= bounds.top && point.y <= bounds.bottom);
    }
    updateLoopConnector(internalConnector) {
        const loopPts = [];
        if (this.edgesMapper.has(internalConnector)) {
            const loopPoints = this.edgesMapper.get(internalConnector);
            if (loopPoints) {
                for (const loopPt of loopPoints) {
                    const pointX = loopPt.x + this.anchorX;
                    const pointY = loopPt.y + this.anchorY;
                    loopPts.push({ x: pointX, y: pointY });
                }
            }
        }
        loopPts.reverse();
        const pts = [];
        const firstPt = loopPts[0];
        const lastPt = loopPts[loopPts.length - 1];
        const sourceNode = this.diagram.nameTable[internalConnector.sourceID];
        const targetNode = this.diagram.nameTable[internalConnector.targetID];
        const srcBounds = sourceNode.wrapper.bounds;
        const tarBounds = targetNode.wrapper.bounds;
        const srcNode = sourceNode;
        const tarNode = targetNode;
        const decisionNode = this.vertexMapper.get(internalConnector.sourceID).isDecisionNode;
        const isYesBranch = this.isYesBranchConnector(internalConnector);
        const isNoBranch = this.isNoBranchConnector(internalConnector);
        const isYesBranchLeft = isYesBranch && this.yesBranchDirection === 'LeftInFlow';
        const isNoBranchRight = isNoBranch && ((this.yesBranchDirection === 'RightInFlow' &&
            (this.noBranchDirection === 'RightInFlow' || this.noBranchDirection === 'SameAsFlow')) ||
            (this.noBranchDirection === 'LeftInFlow' &&
                (this.yesBranchDirection === 'RightInFlow' || this.yesBranchDirection === 'SameAsFlow')));
        const hSpacing = this.horizontalSpacing / 2;
        const vSpacing = this.verticalSpacing / 2;
        const combinedBounds = new Rect().uniteRect(srcNode.wrapper.bounds).uniteRect(tarNode.wrapper.bounds);
        const overlappingNodesInDiagram = this.diagram.nodes.filter((node) => node.id !== tarNode.id && node.id !== srcNode.id && node.wrapper.bounds.intersects(combinedBounds));
        if (this.orientation === 'TopToBottom') {
            const source = Array.from(this.vertexMapper.values())
                .filter((e) => e.item.wrapper.bounds.containsPoint({ x: e.item.wrapper.bounds.x, y: srcNode.offsetY }) &&
                srcNode.id !== e.item.id);
            const target = Array.from(this.vertexMapper.values())
                .filter((e) => e.item.wrapper.bounds.containsPoint({ x: e.item.wrapper.bounds.x, y: tarNode.offsetY }) &&
                tarNode.id !== e.item.id);
            const max = Math.max(...loopPts.map((pt) => pt.x));
            let isSiblingsInRight = false;
            if (decisionNode) {
                if (isYesBranchLeft || isNoBranchRight) {
                    isSiblingsInRight = true;
                }
            }
            else {
                isSiblingsInRight = (source.length > 0 && source[0].item.wrapper.bounds.x > srcBounds.x);
            }
            if (target.length === 0 || (!isSiblingsInRight && target.length > 0 && target.filter((e) => e.item.wrapper.bounds.right < tarBounds.left).length > 0)) {
                // Determine X coordinates based on conditions
                const initialX = (!isSiblingsInRight && max > srcBounds.right) ? srcBounds.right : srcBounds.left;
                const midX = initialX + (isSiblingsInRight ? -hSpacing : hSpacing);
                const targetX = (!isSiblingsInRight && max > tarBounds.right) ? tarBounds.right : tarBounds.left;
                // Add points to the collection
                pts.push({ x: initialX, y: srcNode.offsetY });
                pts.push({ x: midX, y: srcNode.offsetY });
                pts.push({ x: midX, y: tarNode.offsetY });
                pts.push({ x: targetX, y: tarNode.offsetY });
            }
            else {
                const targetBottom = target[0].item.wrapper.bounds.bottom;
                const verticalMiddle = targetBottom + vSpacing;
                const startX = !isSiblingsInRight ? srcBounds.right : srcBounds.left;
                const middleX = startX + (isSiblingsInRight ? -hSpacing : hSpacing);
                const endX = !isSiblingsInRight && max > tarBounds.right ? tarBounds.right + hSpacing : tarBounds.left - hSpacing;
                const finalX = !isSiblingsInRight && max > tarBounds.right ? tarBounds.right : tarBounds.left;
                pts.push({ x: startX, y: srcNode.offsetY });
                pts.push({ x: middleX, y: srcNode.offsetY });
                pts.push({ x: middleX, y: verticalMiddle });
                pts.push({ x: endX, y: verticalMiddle });
                pts.push({ x: endX, y: tarNode.offsetY });
                pts.push({ x: finalX, y: tarNode.offsetY });
            }
            if (overlappingNodesInDiagram.length > 0) {
                const boundsValue = isSiblingsInRight
                    ? Math.min(...overlappingNodesInDiagram.map((node) => node.wrapper.bounds.left))
                    : Math.max(...overlappingNodesInDiagram.map((node) => node.wrapper.bounds.right));
                if ((isSiblingsInRight && boundsValue < pts[1].x) || (!isSiblingsInRight && boundsValue > pts[1].x)) {
                    const newX = boundsValue + (isSiblingsInRight ? -hSpacing : hSpacing);
                    pts[1].x = newX;
                    pts[2].x = newX;
                }
            }
        }
        else {
            const source = Array.from(this.vertexMapper.values())
                .filter((e) => e.item.wrapper.bounds.containsPoint({ y: e.item.wrapper.bounds.y, x: srcNode.offsetX }) &&
                srcNode.id !== e.item.id);
            const target = Array.from(this.vertexMapper.values())
                .filter((e) => e.item.wrapper.bounds.containsPoint({ y: e.item.wrapper.bounds.y, x: tarNode.offsetX }) &&
                tarNode.id !== e.item.id);
            const max = Math.max(...loopPts.map((pt) => pt.y));
            let isSiblingsInBottom = false;
            if (decisionNode) {
                if (isYesBranchLeft || isNoBranchRight) {
                    isSiblingsInBottom = true;
                }
            }
            else {
                isSiblingsInBottom = (source.length > 0 && source[0].item.wrapper.bounds.y > srcBounds.y);
            }
            if (target.length === 0 || (!isSiblingsInBottom && target.length > 0 && target.some((e) => e.item.wrapper.bounds.bottom < tarBounds.top))) {
                pts.push({ x: srcNode.offsetX, y: (!isSiblingsInBottom && max > srcBounds.bottom) ? srcBounds.bottom : srcBounds.top });
                const midY = pts[0].y + (isSiblingsInBottom ? -vSpacing : vSpacing);
                pts.push({ x: srcNode.offsetX, y: midY });
                pts.push({ x: tarNode.offsetX, y: midY });
                pts.push({ x: tarNode.offsetX, y: (!isSiblingsInBottom && max > tarBounds.bottom) ? tarBounds.bottom : tarBounds.top });
            }
            else {
                const targetRight = target[0].item.wrapper.bounds.right + hSpacing;
                const midY = (!isSiblingsInBottom && max > tarBounds.bottom)
                    ? tarBounds.bottom + vSpacing : tarBounds.top - vSpacing;
                pts.push({ x: srcNode.offsetX, y: (!isSiblingsInBottom) ? srcBounds.bottom : srcBounds.top });
                pts.push({ x: srcNode.offsetX, y: pts[0].y + (isSiblingsInBottom ? -vSpacing : vSpacing) });
                pts.push({ x: targetRight, y: pts[1].y });
                pts.push({ x: targetRight, y: midY });
                pts.push({ x: tarNode.offsetX, y: midY });
                pts.push({ x: tarNode.offsetX, y: ((!isSiblingsInBottom && max > tarBounds.bottom) ? tarBounds.bottom : tarBounds.top) });
            }
            if (overlappingNodesInDiagram.length > 0) {
                const boundsValue = isSiblingsInBottom
                    ? Math.min(...overlappingNodesInDiagram.map((e) => e.wrapper.bounds.top))
                    : Math.max(...overlappingNodesInDiagram.map((e) => e.wrapper.bounds.bottom));
                if ((isSiblingsInBottom && pts[1].y > boundsValue) || (!isSiblingsInBottom && pts[1].y < boundsValue)) {
                    const adjustment = isSiblingsInBottom ? -vSpacing : vSpacing;
                    pts[1].y = boundsValue + adjustment;
                    pts[2].y = boundsValue + adjustment;
                }
            }
        }
        this.updatePoints(pts, internalConnector);
    }
    checkForYesOrNoBranch(edge, isYesBranch, isNoBranch) {
        if (edge.annotations && edge.annotations.length) {
            edge.annotations.forEach((annotation) => {
                if (typeof annotation.content === 'string') {
                    const text = annotation.content.toString();
                    this.yesBranchValues.forEach((branchText) => {
                        if (text.localeCompare(branchText, undefined, { sensitivity: 'accent' }) === 0) {
                            isYesBranch = true;
                            return;
                        }
                    });
                    this.noBranchValues.forEach((branchText) => {
                        if (text.localeCompare(branchText, undefined, { sensitivity: 'accent' }) === 0) {
                            isNoBranch = true;
                            return;
                        }
                    });
                }
            });
        }
        return { isYesBranch, isNoBranch };
    }
    getTreeVertices(root, seenVertices, unseenVertices) {
        if (root != null && seenVertices.indexOf(root) === -1) {
            seenVertices.push(root);
            unseenVertices = unseenVertices.filter((vertex) => vertex !== root);
            const children = root.item.outEdges;
            if (children.length === 2) {
                const c1 = this.diagram.nameTable[this.diagram.nameTable[children[0]].targetID];
                const c2 = this.diagram.nameTable[this.diagram.nameTable[children[1]].targetID];
                const childVertex1 = this.vertexMapper.get(c1.id);
                const childVertex2 = this.vertexMapper.get(c2.id);
                if (childVertex1 && childVertex2) {
                    let hasYesChild = childVertex1.isYesChild || childVertex2.isYesChild;
                    let hasNoChild = childVertex1.isNoChild || childVertex2.isNoChild;
                    if (hasYesChild && !hasNoChild) {
                        if (childVertex1.isYesChild) {
                            childVertex2.isNoChild = true;
                        }
                        else {
                            childVertex1.isNoChild = true;
                        }
                        hasNoChild = true;
                    }
                    else if (!hasYesChild && hasNoChild) {
                        if (childVertex1.isNoChild) {
                            childVertex2.isYesChild = true;
                        }
                        else {
                            childVertex1.isYesChild = true;
                        }
                        hasYesChild = true;
                    }
                    root.isDecisionNode = hasYesChild;
                }
            }
            root.outEdges.forEach((outConnector) => {
                const child = this.diagram.nameTable[outConnector.targetID];
                const childVertex = this.vertexMapper.get(child.id);
                if (childVertex != null) {
                    if (!root.isDecisionNode) {
                        childVertex.isYesChild = false;
                        childVertex.isNoChild = false;
                    }
                    this.getTreeVertices(childVertex, seenVertices, unseenVertices);
                }
            });
        }
    }
    /**
     * Initializes the edges collection of the vertices\
     *
     * @returns {  IConnector[] }    Initializes the edges collection of the vertices\
     * @param {FlowChartVertex} node - provide the node value.
     * @private
     */
    getEdges(node) {
        const edges = [];
        if (node !== null && node !== undefined) {
            for (const inEdge of node.inEdges) {
                edges.push(inEdge);
            }
            for (const outEdge of node.outEdges) {
                edges.push(outEdge);
            }
        }
        return edges;
    }
    /**
     * Returns the source/target vertex of the given connector \
     *
     * @returns {  FlowChartVertex }    Returns the source/target vertex of the given connector \
     * @param {IConnector} edge - provide the node value.
     * @param {boolean} source - provide the node value.
     * @private
     */
    getVisibleTerminal(edge, source) {
        //differ from complex-hierarchical-tree.ts
        const nodeWrapper = source ? edge.sourceID : edge.targetID;
        return this.vertexMapper.get(nodeWrapper);
    }
    /**
     * used to get the edges between the given source and target  \
     *
     * @returns {  IConnector[] }    used to get the edges between the given source and target  .\
     * @param {FlowChartVertex} source - provide the angle value.
     * @param { FlowChartVertex} target - provide the angle value.
     * @param { boolean} directed - provide the angle value.
     * @private
     */
    getEdgesBetween(source, target, directed) {
        const edges = this.getEdges(source);
        const result = [];
        for (let i = 0; i < edges.length; i++) {
            const src = this.getVisibleTerminal(edges[parseInt(i.toString(), 10)], true);
            const trg = this.getVisibleTerminal(edges[parseInt(i.toString(), 10)], false);
            if ((src.id === source.id && trg.id === target.id)) {
                result.push(edges[parseInt(i.toString(), 10)]);
            }
        }
        return result;
    }
    /**
     *To destroy the FlowchartLayout
     *
     * @returns {void} To destroy the FlowchartLayout
     */
    destroy() {
        /**
         * Destroys the FlowchartLayout module
         */
    }
    /**
     * @returns { string } toBounds method .\
     * Get getModuleName name.
     */
    getModuleName() {
        /**
         * Returns the module name
         */
        return 'FlowchartLayout';
    }
}
